import type { TaskSnapshot } from '../stores/types';
import { resolveTaskNameOrNull, toWallClockDateTime } from '../utils/utils';
import { computeDurationFromStartAndFinish } from './addTaskUtils';
import {
  applyCalendarDayKeepingTime,
  sameCalendarDay,
} from './ganttDateFormat';
import {
  exclusiveEndToInclusiveFinishWallClock,
} from './mspInclusiveEndBridge';

/**
 * Wall-clock equality for schedule dates.
 * Gantt `date_format` is minute precision; store keeps seconds — compare normalized
 * wall-clock strings, not raw Date ms.
 */
export function sameWallClock(
  a: string | null | undefined,
  b: string | null | undefined,
): boolean {
  if (!a && !b) return true;
  if (!a || !b) return false;
  const wa = toWallClockDateTime(a);
  const wb = toWallClockDateTime(b);
  return !!wa && !!wb && wa === wb;
}

export type GanttTaskScheduleSource = {
  text?: string | null;
  taskName?: string | null;
  name?: string | null;
  task_name?: string | null;
  start_date?: Date | null;
  end_date?: Date | null;
  duration?: number | null;
  progress?: number | null;
  type?: string;
};

/**
 * Build a store patch from a DHTMLX task only for fields that actually changed.
 * Returns null when nothing meaningful moved (format noise / no-op auto-schedule touch).
 * Start/finish use calendar-day equality so date-only editors that rewrite 08:00 → 00:00
 * do not dirty Save.
 *
 * Finish is converted from DHTMLX exclusive end_date → inclusive MSP finish.
 * Duration uses inclusive working-day math (same as Task Details).
 */
export function buildSchedulePatchFromGanttTask(
  ganttTask: GanttTaskScheduleSource,
  existing: TaskSnapshot,
  formatDate: (date: Date | undefined) => string | null,
): Partial<TaskSnapshot> | null {
  const patch: Partial<TaskSnapshot> = {};

  const resolvedName = resolveTaskNameOrNull(ganttTask);
  if (resolvedName != null && resolvedName !== (existing.taskName ?? null)) {
    patch.taskName = resolvedName;
  }

  const newStart = ganttTask.start_date ? formatDate(ganttTask.start_date) : null;
  if (newStart && !sameCalendarDay(newStart, existing.start)) {
    patch.start = applyCalendarDayKeepingTime(ganttTask.start_date!, existing.start);
  }

  const inclusiveFinish = ganttTask.end_date
    ? exclusiveEndToInclusiveFinishWallClock(ganttTask.end_date, existing.finish)
    : null;
  if (inclusiveFinish && !sameCalendarDay(inclusiveFinish, existing.finish)) {
    patch.finish = inclusiveFinish;
  }

  const startForDuration = patch.start ?? existing.start ?? newStart;
  const finishForDuration = patch.finish ?? existing.finish ?? inclusiveFinish;
  if (startForDuration && finishForDuration) {
    const inclusiveDuration = computeDurationFromStartAndFinish(
      startForDuration,
      finishForDuration,
    );
    if (
      Number.isFinite(inclusiveDuration) &&
      inclusiveDuration !== existing.duration
    ) {
      patch.duration = inclusiveDuration;
    }
  } else if (ganttTask.duration != null && Number.isFinite(Number(ganttTask.duration))) {
    const d = Number(ganttTask.duration);
    if (d !== existing.duration) {
      patch.duration = d;
    }
  }

  if (ganttTask.progress != null) {
    const percentComplete = Math.round(Number(ganttTask.progress) * 100);
    if (percentComplete !== existing.percentComplete) {
      patch.percentComplete = percentComplete;
    }
  }

  if (ganttTask.type !== undefined) {
    const milestone = ganttTask.type === 'milestone';
    if (milestone !== existing.milestone) {
      patch.milestone = milestone;
    }
    const summary = ganttTask.type === 'project';
    if (summary !== existing.summary) {
      patch.summary = summary;
    }
  }

  return Object.keys(patch).length > 0 ? patch : null;
}
